import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Paper,
  Container,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Person as PersonIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/layout/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import {
  getDriverLocations,
  trackLocation,
  getLocationSharing,
  updateLocationSharing,
} from '../../services/gps';
import { systemSettingsService } from '../../services/systemSettingsService';

// カスタムマーカーアイコン（車アイコンのSVGをインラインで描画。
// material-icons フォントに依存しないため、フォント未読込でも確実に車アイコンが出る）
const createDriverIcon = (color = '#1976d2') => {
  // ピン（しずく型）の中に白い車アイコンを重ねる
  const html = `
    <div style="position:relative;width:36px;height:48px;">
      <svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 30 18 30s18-16.5 18-30C36 8.06 27.94 0 18 0z" fill="${color}"/>
        <circle cx="18" cy="18" r="13" fill="#ffffff"/>
      </svg>
      <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"
           style="position:absolute;left:8px;top:8px;" fill="${color}">
        <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
      </svg>
    </div>`;
  return L.divIcon({
    className: 'driver-car-icon',
    html,
    iconSize: [36, 48],
    iconAnchor: [18, 48],
    popupAnchor: [0, -44],
  });
};

// ホテル用アイコン（赤系のベッドマーク。ドライバー=青と区別）
const createHotelIcon = (color = '#d32f2f') => {
  const html = `
    <div style="position:relative;width:30px;height:40px;">
      <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0C6.7 0 0 6.7 0 15c0 11.25 15 25 15 25s15-13.75 15-25C30 6.7 23.3 0 15 0z" fill="${color}"/>
        <circle cx="15" cy="15" r="10.5" fill="#ffffff"/>
      </svg>
      <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"
           style="position:absolute;left:7px;top:7px;" fill="${color}">
        <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z"/>
      </svg>
    </div>`;
  return L.divIcon({
    className: 'hotel-icon',
    html,
    iconSize: [30, 40],
    iconAnchor: [15, 40],
    popupAnchor: [0, -36],
  });
};

// ホテルの型（表示に必要な分だけ）
interface HotelPin {
  id: number;
  name: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

// ドライバー位置の型（バックエンド /gps/drivers のレスポンスに対応）
interface DriverLocationView {
  user_id: number;
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  latitude: number;
  longitude: number;
  timestamp: string;
  timestamp_label?: string; // バックエンドが返すJST整形済み時刻（優先表示）
}

// ドライバーロール判定（大文字小文字差を吸収）
const isDriver = (role?: string | null) => (role || '').toLowerCase() === 'driver';

const GPSTracking = () => {
  const { user: authUser } = useAuth();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [driverLocations, setDriverLocations] = useState<DriverLocationView[]>([]);
  // マップに表示するホテルのピン（座標があるもののみ）
  const [hotelPins, setHotelPins] = useState<HotelPin[]>([]);
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(false);
  const [sharingSaving, setSharingSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // マップの設定
  const [mapSettings, setMapSettings] = useState({
    default_latitude: 0,
    default_longitude: 0,
    default_map_radius: 12 // デフォルトのズームレベル
  });

  // 初期マップ中心座標
  const [centerPosition, setCenterPosition] = useState<[number, number]>([0, 0]);

  // システム設定から地図設定を取得
  useEffect(() => {
    const fetchMapSettings = async () => {
      try {
        const settings = await systemSettingsService.getSystemSettings();
        console.log('取得した地図設定:', settings);
        
        const latitude = settings.data.default_map_latitude || 35.6895; // バックアップ値
        
        const longitude = settings.data.default_map_longitude || 139.6917; // バックアップ値
        
        const radius = settings.data.default_map_radius || 1000;
        
        // ズームレベルに変換（値が大きすぎる場合は調整）
        const zoom = radius > 100 ? Math.max(5, 15 - Math.log10(radius)) : 12;
        
        console.log('設定した地図値:', { latitude, longitude, zoom, originalRadius: radius });
        
        setMapSettings({
          default_latitude: latitude,
          default_longitude: longitude,
          default_map_radius: Math.floor(zoom)
        });
        
        setCenterPosition([latitude, longitude]);
      } catch (error) {
        console.error('地図設定の取得に失敗:', error);
        // デフォルト値を設定
        setMapSettings({
          default_latitude: 35.6895,
          default_longitude: 139.6917,
          default_map_radius: 12
        });
        
        setCenterPosition([35.6895, 139.6917]);
      }
    };
    
    fetchMapSettings();
  }, []);

  // アクティブドライバー一覧を取得する（モックには一切フォールバックしない）
  const loadDriverLocations = useCallback(async () => {
    try {
      const locations = await getDriverLocations();
      const list = Array.isArray(locations) ? locations : [];
      // 念のため user_id で重複排除（同一ドライバーが複数返っても1件にする）
      const uniq = new Map<number, any>();
      for (const d of list) {
        if (d && d.user_id != null) uniq.set(d.user_id, d);
      }
      setDriverLocations(Array.from(uniq.values()));
    } catch (error) {
      // 取得失敗時は空にする（モックは表示しない）
      console.warn('アクティブドライバーの取得に失敗しました:', error);
      setDriverLocations([]);
    }
  }, []);

  // ホテル一覧を取得してマップのピンにする（座標があるもののみ）
  useEffect(() => {
    (async () => {
      try {
        const hotels = await systemSettingsService.getHotels();
        const pins = (Array.isArray(hotels) ? hotels : [])
          .filter((h: any) => h.latitude != null && h.longitude != null)
          .map((h: any) => ({
            id: h.id,
            name: h.name,
            address: h.address,
            latitude: Number(h.latitude),
            longitude: Number(h.longitude),
          }));
        setHotelPins(pins);
      } catch (e) {
        console.error('ホテル一覧の取得に失敗:', e);
        setHotelPins([]);
      }
    })();
  }, []);

  // 初期化とユーザー情報の取得＋定期ポーリング
  useEffect(() => {
    if (!authUser) {
      console.warn('有効なユーザー情報が見つかりません');
      setIsLoading(false);
      return;
    }

    setCurrentUser(authUser);

    let timer: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    const init = async () => {
      await loadDriverLocations();
      if (!cancelled) setIsLoading(false);
      // 30秒ごとに最新のアクティブドライバーを再取得
      timer = setInterval(loadDriverLocations, 30000);
    };

    init();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [authUser, loadDriverLocations]);

  // 位置情報の更新
  const updateUserLocation = useCallback(async (position) => {
    try {
      if (!currentUser?.id) {
        console.warn('ユーザーIDが見つかりません');
        return;
      }
      
      const { latitude, longitude } = position.coords;
      try {
        // 位置情報をAPIで更新
        await trackLocation({
          latitude,
          longitude,
          user_id: currentUser.id
        });
      } catch (error) {
        console.warn('位置情報の更新に失敗しました。開発環境のため処理を続行します:', error);
      }
    } catch (error) {
      console.error('位置情報の更新処理に失敗:', error);
    }
  }, [currentUser]);

  // 位置情報取得エラー処理
  const handleLocationError = useCallback((error: GeolocationPositionError) => {
    console.error('位置情報取得エラー:', error);
    // ブラウザで位置情報が拒否された場合は共有を自動オフにする
    if (error.code === error.PERMISSION_DENIED) {
      setIsTrackingEnabled(false);
    }
  }, []);

  // ドライバーの位置共有設定（サーバーの永続フラグ）を初期取得
  useEffect(() => {
    if (!isDriver(currentUser?.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const enabled = await getLocationSharing();
        if (!cancelled) setIsTrackingEnabled(enabled);
      } catch (error) {
        console.warn('位置共有設定の取得に失敗しました:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // 共有ON/OFFに応じて watchPosition を開始・停止する
  useEffect(() => {
    if (!isDriver(currentUser?.role)) return;

    if (!isTrackingEnabled) return;
    if (!navigator.geolocation) return;

    const watch = navigator.geolocation.watchPosition(
      updateUserLocation,
      handleLocationError,
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 27000 }
    );
    return () => {
      navigator.geolocation.clearWatch(watch);
    };
  }, [isTrackingEnabled, currentUser, updateUserLocation, handleLocationError]);

  // トグル操作：サーバーに永続化し、state を更新する
  const handleToggleSharing = useCallback(
    async (next: boolean) => {
      setSharingSaving(true);
      try {
        const saved = await updateLocationSharing(next);
        setIsTrackingEnabled(saved);
        // オフにしたら即座にマップから自分が消えるよう一覧を更新
        if (!saved) {
          try {
            const locations = await getDriverLocations();
            setDriverLocations(Array.isArray(locations) ? locations : []);
          } catch {
            /* 一覧更新失敗は無視（次のポーリングで反映） */
          }
        }
      } catch (error) {
        console.error('位置共有設定の切り替えに失敗:', error);
      } finally {
        setSharingSaving(false);
      }
    },
    []
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '80vh' 
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }}}>
          <Box sx={{ flex: 3 }}>
            <MapContainer 
              key={`map-${centerPosition[0]}-${centerPosition[1]}-${mapSettings.default_map_radius}`}
              center={centerPosition} 
              zoom={mapSettings.default_map_radius} 
              style={{ height: '600px', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {driverLocations.map((driver) => (
                <Marker
                  key={`driver-${driver.user_id}`}
                  position={[driver.latitude, driver.longitude]}
                  icon={createDriverIcon()}
                >
                  <Popup>
                    {driver.username} - {driver.timestamp_label || driver.timestamp}
                  </Popup>
                </Marker>
              ))}
              {/* ホテルのピン（赤） */}
              {hotelPins.map((hotel) => (
                <Marker
                  key={`hotel-${hotel.id}`}
                  position={[hotel.latitude as number, hotel.longitude as number]}
                  icon={createHotelIcon()}
                >
                  <Popup>
                    <strong>{hotel.name}</strong>
                    {hotel.address ? <><br />{hotel.address}</> : null}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                アクティブドライバー（{driverLocations.length}名）
              </Typography>
              {driverLocations.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  位置情報を共有中のドライバーはいません
                </Typography>
              ) : (
                <List>
                  {driverLocations.map((driver) => (
                    <ListItem key={driver.user_id}>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={driver.username}
                        secondary={`最終更新: ${driver.timestamp_label || driver.timestamp}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Box>
        </Box>

        {isDriver(currentUser?.role) && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isTrackingEnabled}
                  onChange={(e) => handleToggleSharing(e.target.checked)}
                  disabled={sharingSaving}
                  color="primary"
                />
              }
              label={isTrackingEnabled ? '位置情報を共有中（オン）' : '位置情報は共有していません（オフ）'}
            />
            <Typography variant="caption" color="text.secondary">
              オンにすると現在地が管理者・配車担当のマップに表示されます
            </Typography>
          </Box>
        )}
      </Container>
    );
  };

  return (
    <DashboardLayout title="GPS追跡">
      <PageHeader 
        title="GPS追跡"
        subtitle="現在位置情報の表示と追跡"
      />
      {renderContent()}
    </DashboardLayout>
  );
};

export default GPSTracking;
